import { StripeDto } from '@flagsync/skyline';
import {
  Check,
  ChevronDown,
  ChevronUp,
  CreditCard,
  Loader,
  Minus,
  Plus,
  Users,
} from 'lucide-react';
import { useState } from 'react';
import * as React from 'react';
import { Link } from 'react-router';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Slider } from '@/components/ui/slider';
import { TypographySmall } from '@/components/ui/typography';
import { useToast } from '@/components/ui/use-toast';

import { cn } from '@/lib/utils';
import { PricingTier } from '@/pages/unauthenticated/pricing/components/types';
import { useGetBillingSessionForUpdate } from '@/services/billing/mutations';

const TierHeader = ({ tier }: { tier: PricingTier }) => (
  <div className="flex items-center justify-between gap-x-4">
    <h3 id={tier.activeTier} className="text-xl font-semibold leading-8">
      {tier.name}
    </h3>
    {tier.featured && <Badge variant="primary">Most popular</Badge>}
  </div>
);

const TierFeatureList = ({ features }: { features: string[] }) => (
  <ul
    role="list"
    className="mt-4 space-y-3 text-sm leading-6 text-muted-foreground"
  >
    {features.map((feature) => (
      <li key={feature} className="flex gap-x-3">
        <Check className="h-6 w-5 flex-none text-primary" aria-hidden="true" />
        {feature}
      </li>
    ))}
  </ul>
);

export const TierCard = ({ tier }: { tier: PricingTier }) => {
  return (
    <Card
      className={cn(
        'border-none p-6 lg:p-8 xl:p-10 bg-halfmast-1100/20 rounded-lg',
        {
          'ring-2 ring-primary': tier.featured,
          'ring-2 ring-border': !tier.featured,
        },
      )}
    >
      <div>
        <TierHeader tier={tier} />

        <p className="mt-4 text-base leading-6 text-muted-foreground">
          {tier.description}
        </p>

        <p className="mt-6 flex items-baseline gap-x-2">
          <span className="text-4xl font-bold tracking-tight font-mono">
            ${tier.price['monthly']}
          </span>
          <span className="font-semibold leading-6 text-muted-foreground">
            per seat / month
          </span>
        </p>

        <Button
          asChild
          size="lg"
          variant="glowing"
          className={cn('mt-6 w-full uppercase')}
        >
          <Link to={tier.href} aria-describedby={tier.activeTier}>
            Start Free Trial
          </Link>
        </Button>

        <TypographySmall as="div" className="mt-8">
          {tier.includes}
        </TypographySmall>

        <TierFeatureList features={tier.mainFeatures} />
      </div>
    </Card>
  );
};

export const TierCardWithSeatSelector = ({
  stripe,
  tier,
  organizationId,
}: {
  stripe: StripeDto | null | undefined;
  tier: PricingTier;
  organizationId: number;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [quantity, setQuantity] = useState<number>(stripe?.seats ?? 1);
  const { toast } = useToast();

  const { mutate, isPending } = useGetBillingSessionForUpdate({
    onSuccess: (url) => {
      window.location.href = url;
    },
    onError: (error) => {
      const { title, message } = error.getErrorMessage({
        title: 'Uh oh! Something went wrong.',
        message: 'There was a problem getting your billing session.',
      });
      toast({
        variant: 'destructive',
        title,
        description: Array.isArray(message) ? message.join(' ') : message,
      });
    },
  });

  const totalPrice = tier.price.monthly * quantity;

  const handleQuantityChange = (value: number[]) => {
    setQuantity(value[0]);
  };

  const incrementQuantity = () => {
    if (tier.maxSeats === null || quantity < tier.maxSeats) {
      setQuantity((q) => q + 1);
    }
  };

  const decrementQuantity = () => {
    if (quantity > 1) {
      setQuantity((q) => q - 1);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutate({ organizationId, planTier: tier.activeTier, quantity });
  };

  return (
    <Card
      className={cn(
        'border-none p-6 lg:p-8 xl:p-10 bg-halfmast-1100/20 rounded-lg',
        {
          'ring-2 ring-primary': tier.featured,
          'ring-2 ring-border': !tier.featured,
        },
      )}
    >
      <form onSubmit={handleSubmit}>
        <div>
          <TierHeader tier={tier} />

          <p className="mt-4 text-base leading-6 text-muted-foreground">
            {tier.description}
          </p>

          <p className="mt-6 flex items-baseline gap-x-2">
            <span className="text-4xl font-bold tracking-tight">
              ${tier.price['monthly']}
            </span>
            <span className="font-semibold leading-6 text-muted-foreground">
              per seat / month
            </span>
          </p>

          <Popover open={isOpen} onOpenChange={setIsOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                className="mt-3 w-full flex items-center justify-between text-sm font-medium transition-colors py-1.5 px-2 rounded-md hover:bg-halfmast-1000 self-start"
              >
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 mr-1.5 opacity-70" />
                  <span>
                    {quantity} {quantity === 1 ? 'seat' : 'seats'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <CreditCard className="h-4 w-4 mr-1.5 opacity-70" />
                  <span>${totalPrice}</span>
                  {isOpen ? (
                    <ChevronUp className="h-4 w-4 ml-1.5 opacity-70" />
                  ) : (
                    <ChevronDown className="h-4 w-4 ml-1.5 opacity-70" />
                  )}
                </div>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="p-4" align="start">
              <div className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium">Seat Selector</h4>
                    <span className="text-xs text-halfmast-200">
                      {tier.maxSeats ? `Maximum ${tier.maxSeats}` : 'Unlimited'}
                    </span>
                  </div>

                  <div className="flex items-center gap-x-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={decrementQuantity}
                      disabled={quantity <= 1}
                    >
                      <span className="sr-only">Decrease</span>
                      <Minus className="text-primary" />
                    </Button>

                    <div className="flex-1 px-2">
                      <Slider
                        value={[quantity]}
                        min={1}
                        max={tier.maxSeats || 200}
                        step={1}
                        onValueChange={handleQuantityChange}
                        className="[&_[role=slider]]:h-5 [&_[role=slider]]:w-5 [&_[role=slider]]:border-emerald-200 [&_[role=slider]]:bg-white [&_[role=slider]]:shadow-md [&_[role=slider]]:border [&_[role=slider]]:border-emerald-200 [&_[role=slider]]:focus:ring-emerald-500 [&_[role=slider]]:focus:ring-offset-0 [&_[role=track]]:bg-emerald-100 [&_[role=range]]:bg-emerald-200"
                      />
                    </div>

                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={incrementQuantity}
                      disabled={
                        tier.maxSeats !== null && quantity >= tier.maxSeats
                      }
                    >
                      <span className="sr-only">Increase</span>
                      <Plus className="text-primary" />
                    </Button>
                  </div>
                </div>

                <div className="pt-2 border-t border-halfmast-600">
                  <div className="flex justify-between text-sm">
                    <span className="text-halfmast-200">Price per seat</span>
                    <span className="font-medium">${tier.price.monthly}</span>
                  </div>
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-halfmast-200">Number of seats</span>
                    <span className="font-medium">{quantity}</span>
                  </div>
                  <div
                    className={cn(
                      'flex justify-between font-medium mt-2 pt-2 border-t border-halfmast-600',
                      'font-merriweather',
                    )}
                  >
                    <span>Total monthly price</span>
                    <span className="text-primary">${totalPrice}</span>
                  </div>
                </div>

                <Button
                  type="button"
                  size="sm"
                  className="w-full"
                  onClick={() => {
                    setIsOpen(false);
                  }}
                >
                  Apply
                </Button>
              </div>
            </PopoverContent>
          </Popover>

          <Button
            size="cta"
            type="submit"
            disabled={isPending || !tier || !quantity}
            className={cn('mt-3 w-full', 'font-merriweather')}
          >
            {isPending ? (
              <>
                <Loader className="mr-2 h-4 w-4 animate-spin" />
                <span>Redirecting</span>
              </>
            ) : (
              <span>Continue</span>
            )}
          </Button>

          <TypographySmall as="div" className="mt-8">
            {tier.includes}
          </TypographySmall>

          <TierFeatureList features={tier.mainFeatures} />
        </div>
      </form>
    </Card>
  );
};
